import { useCallback, useEffect, useRef, useState } from 'react';

import { useDisplayTime } from '../../../../utils/date-time-util';

const INTERVALS = {
	m: 60000,
	h: 3600000,
	stop: Infinity,
};

export function useUpdateTime({
	isoDate,
	relativeTimeStyle = 'narrow',
	useAbbreviation = false,
	compact = false,
}: {
	isoDate?: string;
	relativeTimeStyle?: 'long' | 'short' | 'narrow';
	useAbbreviation?: boolean;
	compact?: boolean;
}): string {
	const timeData = useDisplayTime({ isoDate, relativeTimeStyle, useAbbreviation, compact });
	const [, forceUpdate] = useState({});
	const intervalRef = useRef<ReturnType<typeof setInterval>>();
	const isMountedRef = useRef(false);

	const updateTime = useCallback(() => {
		if (isMountedRef.current) {
			forceUpdate({});
		}
	}, []);

	useEffect(() => {
		// don't set interval if 'stop'
		if (timeData.updateInterval === 'stop') {
			return;
		}

		intervalRef.current = setInterval(updateTime, INTERVALS[timeData.updateInterval]);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [updateTime, timeData.updateInterval]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			clearInterval(intervalRef.current);
		};
	}, []);

	return timeData.value || '';
}
